import {
  EnvironmentProviders,
  inject,
  provideEnvironmentInitializer,
  Provider,
} from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { FuseConfig } from '@fuse/services/config';
import { FUSE_CONFIG } from '@fuse/services/config/config.constants';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { FuseLoadingService } from '@fuse/services/loading';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FusePlatformService } from '@fuse/services/platform';
import { FuseSplashScreenService } from '@fuse/services/splash-screen';
import { FuseUtilsService } from '@fuse/services/utils';

export interface FuseProviderConfig {
  mockApi?: {
    delay?: number;
    service?: unknown;
  };
  fuse?: FuseConfig;
}

/**
 * Fuse provider
 */
export const provideFuse = (
  config: FuseProviderConfig
): (Provider | EnvironmentProviders)[] => {
  // Base providers
  const providers: (Provider | EnvironmentProviders)[] = [
    {
      // Use the 'fill' appearance on Angular Material form fields by default
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'fill',
      },
    },
    {
      provide: FUSE_CONFIG,
      useValue: config?.fuse ?? {},
    },

    // R83. `importProvidersFrom(MatDialogModule)` stood here. The module's only provider is
    // `MatDialog`, and `MatDialog` is `providedIn: 'root'` — so this re-provided, in the root
    // environment injector, the very service that injector already had. Its directives are not
    // affected: `importProvidersFrom` takes providers only, and standalone components import
    // `MatDialogModule` themselves where they use its markup.
    provideEnvironmentInitializer(() => inject(FuseConfirmationService)),

    // A12. `provideHttpClient` was called here a second time, carrying only the loading
    // interceptor; the app's call in app.config now owns the single interceptor list.
    provideEnvironmentInitializer(() => inject(FuseLoadingService)),

    provideEnvironmentInitializer(() => inject(FuseMediaWatcherService)),
    provideEnvironmentInitializer(() => inject(FusePlatformService)),
    provideEnvironmentInitializer(() => inject(FuseSplashScreenService)),
    provideEnvironmentInitializer(() => inject(FuseUtilsService)),
  ];

  // Return the providers
  return providers;
};
